import Amplify from 'aws-amplify';
import AWSAppSyncClient, { AUTH_TYPE } from 'aws-appsync';
import gql from 'graphql-tag';
import {
  PROPERTY_SERVICE_V1_AWS_CONFIG,
  PROPERTY_SERVICE_V2_AWS_CONFIG,
  PROPERTY_SERVICE_V3_AWS_CONFIG,
  PROPERTY_SERVICE_V4_AWS_CONFIG,
} from '../env/Configurations';
import {
  createUiBuilderHamburgerMenu,
  createUiBuilderMenu,
  createUiBuilderPage,
  deleteUiBuilderHamburgerMenuItem,
  deleteUiBuilderMenuItem,
  deleteUIBuilderPage,
  duplicateUIBuilderPage,
  publishPage,
  reOrderUiBuilderMenuItems,
  updateUiBuilderHamburgerMenu,
  updateUiBuilderHamburgerMenuState,
  updateUiBuilderMenu,
  updateUiBuilderPage,
  updateUiBuilderPageConfigs,
} from '../graphql/ui-builder/mutation';
import {
  listFlowOptions,
  listUiBuilderHamburgerMenuItems,
  listUiBuilderMenuItems,
  listUiBuilderPages,
  queryListHotelModuleOptions,
} from '../graphql/ui-builder/queries';
import { getCurrentJwtToken, getEncodedValue } from './authUtils';

Amplify.configure({
  ...PROPERTY_SERVICE_V1_AWS_CONFIG,
});

const propertyServiceV2Client = new AWSAppSyncClient({
  url: PROPERTY_SERVICE_V2_AWS_CONFIG.aws_appsync_graphqlEndpoint,
  region: PROPERTY_SERVICE_V2_AWS_CONFIG.region,
  auth: {
    type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,
    jwtToken: async () => await getCurrentJwtToken(),
  },
  disableOffline: true,
  cacheOptions: {
    addTypename: false,
  },
});

const propertyServiceV3Client = new AWSAppSyncClient({
  url: PROPERTY_SERVICE_V3_AWS_CONFIG.aws_appsync_graphqlEndpoint,
  region: PROPERTY_SERVICE_V3_AWS_CONFIG.region,
  auth: {
    type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,
    jwtToken: async () => await getCurrentJwtToken(),
  },
  disableOffline: true,
  cacheOptions: {
    addTypename: false,
  },
});

const propertyServiceV4Client = new AWSAppSyncClient({
  url: PROPERTY_SERVICE_V4_AWS_CONFIG.aws_appsync_graphqlEndpoint,
  region: PROPERTY_SERVICE_V4_AWS_CONFIG.region,
  auth: {
    type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,
    jwtToken: async () => await getCurrentJwtToken(),
  },
  disableOffline: true,
  cacheOptions: {
    addTypename: false,
  },
});

// UI Builder related api calls
export const getFlows = async (lang) => {
  return await propertyServiceV2Client.query({
    query: gql(listFlowOptions),
    variables: getEncodedValue({
      lang,
    }),
    fetchPolicy: 'network-only',
  });
};

export const getUiBuilderPages = async (hotelId, lang) => {
  return await propertyServiceV2Client.query({
    query: gql(listUiBuilderPages),
    variables: getEncodedValue({
      hotelId: hotelId,
      lang,
    }),
    fetchPolicy: 'network-only',
  });
};

export const createUIBuilderNewPage = async (hotelId, name, lang) => {
  return await propertyServiceV2Client.mutate({
    mutation: gql(createUiBuilderPage),
    variables: getEncodedValue({
      hotelId: hotelId,
      name: name,
      lang,
    }),
  });
};

export const editUIBuilderPage = async (hotelId, id, name, lang) => {
  return await propertyServiceV2Client.mutate({
    mutation: gql(updateUiBuilderPage),
    variables: getEncodedValue({
      hotelId: hotelId,
      name: name,
      id: id,
      lang,
    }),
  });
};

export const deleteUIBuilderPageAPI = async (hotelId, id, lang) => {
  return await propertyServiceV2Client.mutate({
    mutation: gql(deleteUIBuilderPage),
    variables: getEncodedValue({
      hotelId: hotelId,
      id: id,
      lang,
    }),
  });
};

export const duplicateUIBuilderPageAPI = async (hotelId, id, name, lang) => {
  return await propertyServiceV2Client.mutate({
    mutation: gql(duplicateUIBuilderPage),
    variables: getEncodedValue({
      hotelId: hotelId,
      id: id,
      name: name,
      lang,
    }),
  });
};

export const saveUiBuilderPageConfigs = async (hotelId, id, uiConfiguration, lang) => {
  return await propertyServiceV2Client.mutate({
    mutation: gql(updateUiBuilderPageConfigs),
    variables: getEncodedValue({
      hotelId: hotelId,
      uiConfiguration: uiConfiguration,
      id: id,
      lang,
    }),
  });
};

export const publishUiBuilderPage = async (hotelId, id, lang) => {
  return await propertyServiceV2Client.mutate({
    mutation: gql(publishPage),
    variables: getEncodedValue({
      hotelId: hotelId,
      id: id,
      lang,
    }),
  });
};

export const getUIBuilderHotelModuleOptions = async (lang) => {
  return await propertyServiceV3Client.query({
    query: gql(queryListHotelModuleOptions),
    variables: getEncodedValue({
      lang,
    }),
    fetchPolicy: 'network-only',
  });
};

export const getUiBuilderMenuItems = async (hotelId, lang) => {
  return await propertyServiceV3Client.query({
    query: gql(listUiBuilderMenuItems),
    variables: getEncodedValue({
      hotelId: hotelId,
      lang,
    }),
    fetchPolicy: 'network-only',
  });
};

export const saveUiBuilderMenu = async (payload, lang) => {
  return await propertyServiceV3Client.mutate({
    mutation: gql(createUiBuilderMenu),
    variables: getEncodedValue({
      hotelId: payload.hotelId,
      name: payload.name,
      pageId: payload.pageId,
      lang,
    }),
  });
};

export const editUiBuilderMenu = async (payload, lang) => {
  return await propertyServiceV3Client.mutate({
    mutation: gql(updateUiBuilderMenu),
    variables: getEncodedValue({
      id: payload.id,
      hotelId: payload.hotelId,
      name: payload.name,
      pageId: payload.pageId,
      lang,
    }),
  });
};

export const deleteUIBuilderMenu = async (id, hotelId, lang) => {
  return await propertyServiceV3Client.mutate({
    mutation: gql(deleteUiBuilderMenuItem),
    variables: getEncodedValue({
      id: id,
      hotelId: hotelId,
      lang,
    }),
  });
};

export const reorderUIBuilderMenu = async (hotelId, menuList, lang) => {
  return await propertyServiceV3Client.mutate({
    mutation: gql(reOrderUiBuilderMenuItems),
    variables: getEncodedValue({
      hotelId: hotelId,
      menuList: menuList,
      lang,
    }),
  });
};

export const getUiBuilderHamburgerMenu = async (hotelId, lang) => {
  return await propertyServiceV4Client.query({
    query: gql(listUiBuilderHamburgerMenuItems),
    variables: getEncodedValue({
      hotelId: hotelId,
      lang,
    }),
    fetchPolicy: 'network-only',
  });
};
export const saveUiBuilderHamburgerMenu = async (payload, lang) => {
  return await propertyServiceV4Client.mutate({
    mutation: gql(createUiBuilderHamburgerMenu),
    variables: getEncodedValue({
      hotelId: payload?.hotelId,
      name: payload?.name,
      pageId: payload?.pageId,
      category: payload?.category,
      redirectOptions: payload?.redirect,
      externalLink: payload?.external,
      flow: payload?.flow,
      isActive: true,
      menuIconUrl: payload?.menuIconUrl,
      lang,
    }),
  });
};

export const editUiBuilderHamburgerMenu = async (payload, lang) => {
  return await propertyServiceV4Client.mutate({
    mutation: gql(updateUiBuilderHamburgerMenu),
    variables: getEncodedValue({
      id: payload?.id,
      hotelId: payload?.hotelId,
      name: payload?.name,
      pageId: payload?.pageId,
      category: payload?.category,
      redirectOptions: payload?.redirect,
      externalLink: payload?.external,
      flow: payload?.flow,
      isActive: true,
      menuIconUrl: payload?.menuIconUrl,
      lang,
    }),
  });
};

export const deleteUIBuilderHamburgerMenu = async (id, hotelId, lang) => {
  return await propertyServiceV4Client.mutate({
    mutation: gql(deleteUiBuilderHamburgerMenuItem),
    variables: getEncodedValue({
      id: id,
      hotelId: hotelId,
      lang,
    }),
  });
};

export const changeHamburgerMenuItemState = async (payload, lang) => {
  return await propertyServiceV4Client.mutate({
    mutation: gql(updateUiBuilderHamburgerMenuState),
    variables: getEncodedValue({
      id: payload?.id,
      hotelId: payload?.hotelId,
      status: payload?.status,
      lang,
    }),
  });
};
